import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { getContent } from "@/lib/sections";
import { CookieBanner } from "@/components/CookieBanner";
import { SubscriptionBanner } from "@/components/SubscriptionBanner";
import { LogOut, Scissors } from "lucide-react";

export function Brand({ className = "" }) {
  const { slug } = useParams();
  const [logoUrl, setLogoUrl] = useState("");
  useEffect(() => {
    api.get(slug ? `/salon/${slug}` : "/salon").then((r) => setLogoUrl(r.data?.salon?.logo_url || "")).catch(() => {});
  }, [slug]);
  return (
    <Link to={slug ? `/salon/${slug}` : "/"} className={`inline-flex items-center gap-3 leading-none ${className}`} data-testid="brand-logo">
      {logoUrl ? (
        <img src={logoUrl} alt="Barber Atelier" className="h-20 w-20 sm:h-24 sm:w-24 object-cover rounded-full border-2 border-[#C5A059]/60 shadow-[0_0_20px_rgba(197,160,89,0.25)]" />
      ) : null}
      <div className="flex flex-col">
        <span className="font-display text-3xl tracking-[0.15em] text-[#F4F0EA]">BARBER</span>
        <span className="font-mono-caps text-[10px] tracking-[0.45em] text-[#C5A059] mt-1">ATELIER</span>
      </div>
    </Link>
  );
}

export function PublicNav() {
  const { slug } = useParams();
  const base = slug ? `/salon/${slug}` : "";
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0908]/80 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-28 flex items-center justify-between">
        <Brand />
        <nav className="hidden md:flex items-center gap-10 text-sm">
          <a href="#prestations" className="text-[#A39E93] hover:text-[#F4F0EA] transition" data-testid="nav-services">Prestations</a>
          <a href="#gallery" className="text-[#A39E93] hover:text-[#F4F0EA] transition" data-testid="nav-gallery">Galerie</a>
          <a href="#about" className="text-[#A39E93] hover:text-[#F4F0EA] transition" data-testid="nav-about">L'atelier</a>
          <a href="#contact" className="text-[#A39E93] hover:text-[#F4F0EA] transition" data-testid="nav-contact">Contact</a>
        </nav>
        <Link to={`${base}/reservation`} className="btn-primary text-[10px] sm:text-xs px-4 sm:px-6 py-3" data-testid="nav-book-btn">
          <Scissors className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="hidden sm:inline">Prendre RDV</span>
          <span className="sm:hidden">RDV</span>
        </Link>
      </div>
    </header>
  );
}

export function OwnerNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0908]/85 border-b border-[#2C2A26]">
      <div className="max-w-7xl mx-auto px-6 h-24 flex items-center justify-between">
        <div className="flex items-center gap-10">
          <Brand />
          <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.2em]">
            <NavLink to="/dashboard" end className={({isActive})=>isActive?"text-[#C5A059]":"text-[#A39E93] hover:text-[#F4F0EA]"} data-testid="nav-dashboard">Planning</NavLink>
            <NavLink to="/dashboard/clients" className={({isActive})=>isActive?"text-[#C5A059]":"text-[#A39E93] hover:text-[#F4F0EA]"} data-testid="nav-clients">Clients</NavLink>
            <NavLink to="/dashboard/stats" className={({isActive})=>isActive?"text-[#C5A059]":"text-[#A39E93] hover:text-[#F4F0EA]"} data-testid="nav-stats">Statistiques</NavLink>
            <NavLink to="/dashboard/billing" className={({isActive})=>isActive?"text-[#C5A059]":"text-[#A39E93] hover:text-[#F4F0EA]"} data-testid="nav-billing">Abonnement</NavLink>
            <NavLink to="/dashboard/settings" className={({isActive})=>isActive?"text-[#C5A059]":"text-[#A39E93] hover:text-[#F4F0EA]"} data-testid="nav-settings">Paramètres</NavLink>
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden sm:inline text-xs text-[#A39E93]">{user?.email}</span>
          <button onClick={handleLogout} className="text-[#A39E93] hover:text-[#C5A059] transition flex items-center gap-2 text-xs uppercase tracking-[0.2em]" data-testid="logout-btn">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Déconnexion
          </button>
        </div>
      </div>
      <SubscriptionBanner />
    </header>
  );
}

export function Footer() {
  const { slug } = useParams();
  const [salon, setSalon] = useState(null);
  useEffect(() => {
    api.get(slug ? `/salon/${slug}` : "/salon").then((r) => setSalon(r.data?.salon || null)).catch(() => {});
  }, [slug]);
  const C = getContent(salon || {});
  const year = new Date().getFullYear();
  const name = salon?.name || "Barber Atelier";
  const copyright = (C.footer_copyright || "").replace(/\{year\}/g, year).replace(/\{name\}/g, name);
  const base = slug ? `/salon/${slug}` : "";
  return (
    <footer className="border-t border-[#2C2A26] mt-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 flex flex-col gap-8">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <Brand />
          <nav className="flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.2em] text-[#A39E93]" data-testid="footer-legal-nav">
            <Link to={`${base}/mentions-legales`} className="hover:text-[#C5A059]" data-testid="footer-mentions-link">Mentions légales</Link>
            <Link to={`${base}/confidentialite`} className="hover:text-[#C5A059]" data-testid="footer-privacy-link">Confidentialité</Link>
            <Link to={`${base}/cgv`} className="hover:text-[#C5A059]" data-testid="footer-cgv-link">CGV</Link>
            <Link to="/login" className="hover:text-[#C5A059]" data-testid="footer-owner-link">{C.footer_owner_link}</Link>
          </nav>
        </div>
        <div className="tracking-wider uppercase text-xs text-[#6B6760]" data-testid="footer-copyright">{copyright}</div>
      </div>
      <CookieBanner />
    </footer>
  );
}
